import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  Search, Home, Bookmark, Clock, GraduationCap, CreditCard, Settings, Grid3x3,
  Plus, User, Sun, Monitor, Upload, Play, ArrowLeft
} from 'lucide-react';

const THUMBNAILS = [
  'bg-gradient-to-br from-[#4796E3] to-[#60a5fa]',
  'bg-gradient-to-br from-[#008744] to-[#10b981]',
  'bg-gradient-to-br from-[#ffa700] to-[#f59e0b]',
  'bg-gradient-to-br from-[#d62d20] to-[#ef4444]',
  'bg-gradient-to-br from-[#4796E3] to-[#008744]',
  'bg-gradient-to-br from-[#ffa700] to-[#d62d20]',
];

const AIEducation = () => {
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');

  const coursesRaw = t('aiEducation.courses') || [];
  const courses = Array.isArray(coursesRaw) ? coursesRaw.map((c, i) => ({
    ...c,
    levelLabel: c.level ? t('aiEducation.' + c.level) : c.level,
    thumbnail: THUMBNAILS[i % THUMBNAILS.length],
  })) : [];

  const sidebarItems = [
    { icon: Home, label: t('aiEducation.sidebarHome'), active: true },
    { icon: Bookmark, label: t('aiEducation.sidebarBookmark') },
    { icon: Clock, label: t('aiEducation.sidebarHistory') },
    { icon: GraduationCap, label: t('aiEducation.sidebarCourses') },
    { icon: CreditCard, label: t('aiEducation.sidebarSubscribe') },
    { icon: Settings, label: t('aiEducation.sidebarSettings') },
    { icon: Grid3x3, label: t('aiEducation.sidebarStudio') },
    { icon: ArrowLeft, label: t('aiEducation.backToEducation'), link: '/education' },
  ];

  const filteredCourses = searchQuery
    ? courses.filter(c => (c.title || '').toLowerCase().includes(searchQuery.toLowerCase()) || (c.description || '').toLowerCase().includes(searchQuery.toLowerCase()))
    : courses;

  return (
    <div className="-mt-16 min-h-screen bg-dark flex flex-col">
      {/* Header */}
      <header className="bg-dark-card border-b border-dark-border sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-6">
            <Link to="/education" className="text-white font-bold text-xl">{t('aiEducation.brandName')}</Link>
          </div>
          <div className="flex-1 max-w-2xl mx-8">
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Search className="h-5 w-5 text-gray-500 group-focus-within:text-accent transition-colors" /></div>
              <input type="text" placeholder={t('aiEducation.searchPlaceholder')} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-4 py-2 bg-dark-surface border border-dark-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent/50 transition-all duration-300" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="p-2 hover:bg-dark-surface rounded-lg transition-colors"><Sun className="h-5 w-5 text-gray-500" /></button>
            <button className="p-2 hover:bg-dark-surface rounded-lg transition-colors"><Monitor className="h-5 w-5 text-gray-500" /></button>
            <button className="flex items-center gap-2 px-4 py-2 bg-dark-surface hover:bg-dark-elevated text-gray-300 rounded-lg transition-colors text-sm font-medium border border-dark-border"><Plus className="h-4 w-4" />{t('aiEducation.create')}</button>
            <button className="flex items-center gap-2 px-4 py-2 bg-white text-dark rounded-lg hover:shadow-glow-white transition-all duration-300 text-sm font-medium"><User className="h-4 w-4" />{t('aiEducation.login')}</button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-64 bg-dark border-r border-dark-border overflow-y-auto">
          <nav className="p-4">
            <ul className="space-y-0.5">
              {sidebarItems.map((item, index) => {
                const Icon = item.icon;
                const cls = `w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 text-[13px] ${item.active ? 'bg-accent/10 text-accent font-medium' : 'text-gray-400 hover:bg-dark-surface hover:text-gray-200'}`;
                const content = <><Icon className="h-4 w-4" /><span>{item.label}</span></>;
                return (
                  <li key={index}>
                    {item.link ? <Link to={item.link} className={cls}>{content}</Link> : <button className={cls}>{content}</button>}
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>

        <main className="flex-1 overflow-y-auto">
          {/* Hero */}
          <section className="relative min-h-[55vh] flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0 bg-gradient-mesh" />
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-[#4796E3]/12 via-[#4796E3]/08 to-transparent rounded-full blur-[100px]" />
              <div className="absolute -bottom-20 -left-20 w-[500px] h-[500px] bg-gradient-to-tr from-amber-600/8 to-transparent rounded-full blur-[100px]" />
            </div>
            <div className="relative z-10 text-center px-4">
              <h1 className="text-4xl md:text-5xl font-bold text-gradient-hero mb-4 tracking-tight">{t('aiEducation.title')}</h1>
              <p className="text-gray-400 text-lg md:text-xl mb-8">{t('aiEducation.heroSubline')}</p>
              <div className="max-w-2xl mx-auto">
                <div className="border-gradient bg-dark-card rounded-2xl p-8 mb-4 shadow-dark-card">
                  <div className="flex items-center justify-center gap-4">
                    <Plus className="h-8 w-8 text-gray-600" />
                    <span className="text-gray-400 text-lg">{t('aiEducation.pasteVideoPrompt')}</span>
                  </div>
                </div>
                <button className="w-12 h-12 bg-dark-surface hover:bg-dark-elevated rounded-full flex items-center justify-center transition-all duration-300 mx-auto border border-dark-border hover:border-dark-border-light">
                  <Upload className="h-6 w-6 text-gray-400" />
                </button>
              </div>
              <div className="absolute top-4 right-4">
                <span className="inline-flex items-center px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-accent bg-accent/10 border border-accent/20 rounded-full">{t('aiEducation.inDevelopment')}</span>
              </div>
            </div>
          </section>

          {/* Courses */}
          <section className="p-6 bg-dark-card border-t border-dark-border">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">{t('aiEducation.coursesTitle')} &gt;</h2>
            </div>
            <div className="overflow-x-auto pb-4">
              <div className="flex gap-4" style={{ width: 'max-content' }}>
                {filteredCourses.map((course, index) => (
                  <div key={index} className="w-80 flex-shrink-0 bg-dark-surface border border-dark-border rounded-xl overflow-hidden hover:border-dark-border-light transition-all duration-400 cursor-pointer group glow-card">
                    <div className={`${course.thumbnail} h-48 relative flex items-center justify-center`}>
                      <Play className="h-16 w-16 text-white/80 group-hover:scale-110 transition-transform duration-300" />
                      <div className="absolute top-2 right-2"><span className="px-2 py-1 bg-black/60 text-white text-xs rounded">{course.duration}</span></div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-start gap-3 mb-2">
                        <div className="w-8 h-8 bg-dark-elevated rounded-full flex-shrink-0 border border-dark-border" />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-white mb-1 line-clamp-2 group-hover:text-accent transition-colors text-sm">{course.title}</h3>
                          <p className="text-xs text-gray-500 mb-1">{course.instructor}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span>{t('aiEducation.lessonsCount', { n: course.lessons })}</span><span>•</span><span>{t('aiEducation.updatedAt', { time: course.updated })}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-end mt-3 pt-3 border-t border-dark-border/50">
                        <button className="px-4 py-1.5 bg-accent/10 hover:bg-accent/20 text-accent text-sm rounded-lg transition-colors font-medium">{t('aiEducation.viewDetails')}</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {filteredCourses.length === 0 && <div className="text-center py-12"><p className="text-gray-500">{t('aiEducation.noCoursesFound')}</p></div>}
          </section>
        </main>
      </div>
    </div>
  );
};

export default AIEducation;
